import React, { useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import PropTypes from "prop-types";
import xGreenIcon from "../../assets/svgs/x-green.svg";
import "./index.scss";

// Shared modal shell — overlay + header (title + close X) + body + footer — that
// renders via createPortal so it escapes any parent `overflow: hidden`. Mirrors
// the Export/Import modals' bulk-io pattern. Used by every cohort action modal
// (Archive/Activate, Update TTL, Copy, Push, Pull, Delete) so they share one
// visual identity.
//
// Footer is rendered as `footer` prop (not children) so the body content stays
// scrollable while the footer stays pinned. ESC closes the modal; focus is
// trapped inside the surface and the first focusable element gets initial focus.
function ModalSurface({ title, onClose, footer, children, closeDisabled = false }) {
	const surfaceRef = useRef(null);

	useEffect(() => {
		const surface = surfaceRef.current;
		if (!surface) return undefined;

		const getFocusable = () =>
			surface.querySelectorAll(
				'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
			);

		const focusables = getFocusable();
		focusables[0]?.focus();

		const handleKey = (e) => {
			if (e.key === "Escape" && !closeDisabled) {
				e.stopPropagation();
				onClose();
				return;
			}
			if (e.key === "Tab") {
				const current = getFocusable();
				if (current.length === 0) return;
				const first = current[0];
				const last = current[current.length - 1];
				if (e.shiftKey && document.activeElement === first) {
					e.preventDefault();
					last.focus();
				} else if (!e.shiftKey && document.activeElement === last) {
					e.preventDefault();
					first.focus();
				}
			}
		};

		document.addEventListener("keydown", handleKey);
		return () => document.removeEventListener("keydown", handleKey);
	}, [onClose, closeDisabled]);

	return createPortal(
		<div className="bulk-io-overlay">
			<div className="bulk-io-surface bulk-io-surface--compact" ref={surfaceRef}>
				<header className="bulk-io-topbar">
					<h2>{title}</h2>
					<button
						type="button"
						className="bulk-io-close"
						aria-label="Close"
						onClick={onClose}
						disabled={closeDisabled}
					>
						<img src={xGreenIcon} alt="Close" />
					</button>
				</header>
				<div className="bulk-io-body">{children}</div>
				{footer && (
					<footer className="bulk-io-footer bulk-io-footer--end">
						<div className="bulk-io-footer__actions">{footer}</div>
					</footer>
				)}
			</div>
		</div>,
		document.body
	);
}

ModalSurface.propTypes = {
	title: PropTypes.node.isRequired,
	onClose: PropTypes.func.isRequired,
	footer: PropTypes.node,
	children: PropTypes.node,
	closeDisabled: PropTypes.bool,
};

export default ModalSurface;
